import React from 'react'
import { Map } from 'immutable'
import PropTypes from 'prop-types'
import toNumber from 'lodash/toNumber'
import get from 'lodash/get'
import { getBoundActions } from 'actions'
import { connect as connectRedux } from 'react-redux'
import { deactivateSubject } from 'services/testarossa'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { showPaymentErrorMessage } from 'services/user-account'
import { format as formatDateTime, getDateTime } from 'services/date-time'
import { TestarossaSwitch, TestarossaCase, TestarossaDefault } from 'components/Testarossa'
import { headerAlertBarShouldRender } from 'components/AlertBar/connect'
import { WithLazy } from 'components/App/Hydration'
import AlertBar from 'components/AlertBar'
import { URL_ACCOUNT_UPDATE_PAYMENT, URL_AI_CHAT } from 'services/url/constants'
import { PLAN_CATEGORY_AI_ONLY } from 'services/plans'

const AlertBarAnonymousLanguage = React.lazy(() => import('components/AlertBarAnonymousLanguage'))
const AlertBarFreeTrialExpired = React.lazy(() => import('components/AlertBarFreeTrialExpired'))
const AlertBarPaymentError = React.lazy(() => import('components/AlertBarPaymentError'))
const AlertBarBrowseOnly = React.lazy(() => import('components/AlertBarBrowseOnly'))

function onDismissMemberRibbon () {
  deactivateSubject({ campaign: 'RI-97' })
}

function onDismissAnonRibbon () {
  deactivateSubject({ campaign: 'RI-98' })
}

function renderNotification (campaign, variation, onDismiss) {
  const { data = {} } = variation
  const { html = '' } = data
  const innerHtml = { __html: html }
  /* eslint-disable react/no-danger */
  return html ? (
    <AlertBar dismissable onDismiss={onDismiss}>
      <span dangerouslySetInnerHTML={innerHtml} />
    </AlertBar>
  ) : null
  /* eslint-enable */
}

class AlertBarLoader extends React.Component {
  showFreeTrial = () => {
    const { props } = this
    const {
      auth, user, userAccount, page,
    } = props
    const zuoraSubscription = userAccount.getIn(['details', 'data', 'billing', 'subscriptions'], Map())
    const zuoraAccountId = zuoraSubscription.get('billingAccountId')
    const noZuoraAccount = zuoraSubscription.size > 0 && !zuoraAccountId
    const isFreeTrial = user.getIn(['data', 'freeTrialNoBillingInfo', 'ratePlanId'])
    const isLoggedIn = !!auth.get('jwt')
    const dateFormatString = 'MM/DD/YY'
    const locale = page.get('locale')
    const endDateString = user.getIn(['data', 'userEntitlements', 'end'])
    const endDateInMilliseconds = parseInt(endDateString, 10) * 1000
    const formattedEndDate = new Date(endDateInMilliseconds)
    const getEndDate = getDateTime(formattedEndDate)
    const reformattedEndDate = formatDateTime(getEndDate, locale, dateFormatString)
    const remainingTime = Date.parse(reformattedEndDate) - Date.now()
    return isLoggedIn && isFreeTrial && noZuoraAccount && remainingTime < 0
  }

  renderAnonAlerts = () => {
    const { props } = this
    const { alertBar, auth } = props
    const isAuthorized = auth.get('jwt')
    const anonLang = alertBar.getIn(['data', 'anonymousLanguage'])
    const visible = alertBar.get('visible')

    if (visible && anonLang && !isAuthorized) {
      const { setUserDataLanguageAlertBarDeclined } = props
      const onClick = alertBar.getIn(['data', 'anonymousLanguage'])
        ? setUserDataLanguageAlertBarDeclined
        : undefined
      return (
        <AlertBar dismissable onClose={onClick}>
          <WithLazy>
            <AlertBarAnonymousLanguage history={props.history} />
          </WithLazy>
        </AlertBar>
      )
    }
    return null
  }

  renderMemberAlerts = () => {
    const { props } = this
    const {
      auth,
      user,
      alertBar,
      userAccount,
      userProfiles,
      location,
    } = props
    const isPrimaryUser = toNumber(userProfiles.getIn(['data', 0, 'uid'])) === user.getIn(['data', 'uid'])
    const userPlanCategory = userAccount.getIn(['details', 'data', 'billing', 'subscriptions', 'plan', 'planCategory'])
    const paymentError = showPaymentErrorMessage({ userAccount })
    const dismissed = alertBar.get('dismissed')

    if (location && !headerAlertBarShouldRender(location)) {
      return null
    }

    if (auth.get('jwt') && get(location, 'pathname') !== URL_AI_CHAT && userPlanCategory === PLAN_CATEGORY_AI_ONLY) {
      return (
        <AlertBar className="alert-bar__alert-bar-browse-only">
          <WithLazy>
            <AlertBarBrowseOnly />
          </WithLazy>
        </AlertBar>
      )
    }

    if (this.showFreeTrial()) {
      return (
        <AlertBar error>
          <WithLazy>
            <AlertBarFreeTrialExpired />
          </WithLazy>
        </AlertBar>
      )
    }

    if (auth.get('jwt') && paymentError && isPrimaryUser && !dismissed && get(location, 'pathname') !== URL_ACCOUNT_UPDATE_PAYMENT) {
      return (
        <AlertBar error dismissable>
          <WithLazy>
            <AlertBarPaymentError />
          </WithLazy>
        </AlertBar>
      )
    }
    return null
  }

  renderAlert = () => {
    const { props } = this
    const { auth } = props
    const isAuthorized = auth.get('jwt')

    if (!isAuthorized) {
      return this.renderAnonAlerts()
    }
    return this.renderMemberAlerts()
  }

  render () {
    return this.renderAlert() || (
      <TestarossaSwitch>
        <TestarossaCase campaign="RI-97">
          {(campaign, variation) => (
            renderNotification(campaign, variation, onDismissMemberRibbon)
          )}
        </TestarossaCase>
        <TestarossaCase campaign="RI-98">
          {(campaign, variation) => (
            renderNotification(campaign, variation, onDismissAnonRibbon)
          )}
        </TestarossaCase>
        <TestarossaDefault unwrap>
          {null}
        </TestarossaDefault>
      </TestarossaSwitch>
    )
  }
}

AlertBarLoader.propTypes = {
  alertBar: ImmutablePropTypes.map.isRequired,
  userAccount: ImmutablePropTypes.map.isRequired,
  location: PropTypes.object,
  history: PropTypes.object,
}

export default connectRedux(
  (state) => ({
    userProfiles: state.userProfiles,
    userAccount: state.userAccount,
    alertBar: state.alertBar,
    user: state.user,
    auth: state.auth,
    page: state.page,
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setUserDataLanguageAlertBarDeclined:
        actions.user.setUserDataLanguageAlertBarDeclined,
    }
  },
)(AlertBarLoader)
